import { spaceAPI } from '@/api';
import HeaderComponent from './ResomHeader';
import { palette } from '@/modules/defines/styles';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface EventDataType {
  id: number;
  title: string;
  description: string;
  date: string;
  publish: boolean;
  img: string;
  link: string;
  updatedAt?: string;
}

const ResomEventDetailContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<EventDataType>>({});
  const [initialData, setInitialData] = useState<Partial<EventDataType>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: eventDataAPI, isLoading } = useQuery({
    queryKey: ['getEventResom', id],
    queryFn: () =>
      spaceAPI.getEventResom({
        code: import.meta.env.VITE_SECRET_API_KEY,
        id: Number(id),
      }),
  });
  const updateMutation = useMutation({
    mutationFn: (data: Partial<EventDataType>) =>
      spaceAPI.updateEventResom(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
        },
        {
          id: Number(id),
          ...data,
        }
      ),
    onSuccess: () => {
      alert('수정되었습니다');
      navigate('/resom/event');
    },
  });

  // 이미지 업로드 뮤테이션 (API 구현 필요)
  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('img', file);
      return spaceAPI.uploadEventResomImage(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
          id: id,
        },
        formData
      );
    },
    onSuccess: (response) => {
      if (response?.data?.img) {
        setFormData((prev) => ({
          ...prev,
          img: response.data.img,
        }));
        alert('이미지가 업로드되었습니다.');
      }
    },
    onError: () => {
      alert('이미지 업로드에 실패했습니다.');
    },
  });

  useEffect(() => {
    if (eventDataAPI?.data) {
      setFormData(eventDataAPI.data);
      setInitialData(eventDataAPI.data);
    }
  }, [eventDataAPI]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const isFormChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadImageMutation.mutate(e.target.files[0]);
    }
  };
  const handleToggleSwitch = () => {
    setFormData((prev) => ({
      ...prev,
      publish: !prev.publish,
    }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      img: '',
    }));
  };

  if (isLoading) {
    return <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>;
  }

  return (
    <Container>
      <HeaderComponent />
      <Content>
        <ContentWrapper>
          <TabContainer>
            <h1>이벤트 상세</h1>
            <SaveButton
              type="button"
              onClick={handleSubmit}
              disabled={!isFormChanged()}
            >
              저장
            </SaveButton>
          </TabContainer>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <SwitchLabel>
                <Label>앱에서 사용</Label>
                <SwitchWrapper onClick={handleToggleSwitch}>
                  <SwitchInput
                    type="checkbox"
                    checked={formData.publish || false}
                    onChange={handleToggleSwitch}
                  />
                  <SwitchSlider className={formData.publish ? 'checked' : ''} />
                  <SwitchStatus>{formData.publish ? 'ON' : 'OFF'}</SwitchStatus>
                </SwitchWrapper>
              </SwitchLabel>
              <Description>
                앱에서 해당 공간을 표시할지 여부를 설정합니다
              </Description>
            </FormGroup>

            <FormGroup>
              <Label>제목</Label>
              <Input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={handleChange}
              />
              <Description>이벤트의 제목을 입력해주세요</Description>
            </FormGroup>

            <FormGroup>
              <Label>설명</Label>
              <TextArea
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
              />
              <Description>
                이벤트에 대한 자세한 설명을 입력해주세요
              </Description>
            </FormGroup>

            <FormGroup>
              <Label>날짜</Label>
              <Input
                type="text"
                name="date"
                value={formData.date || ''}
                onChange={handleChange}
              />
              <Description>이벤트 날짜를 입력해주세요</Description>
            </FormGroup>

            <FormGroup>
              <Label>이미지</Label>
              <ImageContainer>
                {formData.img && formData.img !== 'null' ? (
                  <ImagePreviewWrapper>
                    <ImagePreview src={formData.img} alt="이벤트 이미지" />
                    <RemoveImageButton onClick={handleRemoveImage}>
                      ✕
                    </RemoveImageButton>
                  </ImagePreviewWrapper>
                ) : (
                  <ImagePlaceholder onClick={handleImageUploadClick}>
                    <PlaceholderText>클릭하여 이미지 업로드</PlaceholderText>
                  </ImagePlaceholder>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </ImageContainer>
              <Description>이벤트의 대표 이미지를 업로드해주세요</Description>
            </FormGroup>

            <FormGroup>
              <Label>링크</Label>
              <Input
                type="text"
                name="link"
                value={formData.link || ''}
                onChange={handleChange}
              />
              <Description>
                이벤트와 관련된 웹페이지 링크를 입력해주세요
              </Description>
            </FormGroup>
          </Form>
        </ContentWrapper>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const Content = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: #f5f5f5;
  overflow-y: scroll;
  height: calc(100vh - 120px);
  position: relative;
`;

const ContentWrapper = styled.div`
  overflow-y: scroll;
  height: calc(100vh - 120px);
  position: relative;
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #1e293b;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${palette.main900};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const Description = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.25rem;
  margin-bottom: 0;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${palette.main900};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ImageContainer = styled.div`
  margin-top: 0.5rem;
`;

const ImagePreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  overflow: hidden;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  max-width: 300px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f5f9;
  }
`;

const PlaceholderText = styled.span`
  color: #94a3b8;
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #666;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 2rem;
`;

const SaveButton = styled.button`
  padding: 0.5rem 1.5rem;
  background-color: ${palette.main900};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: ${palette.main800};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SwitchLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SwitchWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const SwitchInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;
  position: absolute;
`;

const SwitchSlider = styled.span`
  cursor: pointer;
  width: 50px;
  height: 26px;
  background-color: #ccc;
  display: inline-block;
  border-radius: 26px;
  position: relative;
  transition: 0.4s;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    border-radius: 50%;
    transition: 0.4s;
  }

  &.checked {
    background-color: ${palette.main900};
  }

  &.checked:before {
    transform: translateX(24px);
  }
`;

const SwitchStatus = styled.span`
  margin-left: 10px;
  font-weight: 600;
  color: #1e293b;
`;

export default ResomEventDetailContainer;
